import React from "react";
import { useTranslation } from "react-i18next";
import NutritionHistoryTableRow from "./NutritionHistoryTableRow";

import {
  getNutritionData,
  getNutritionPlan,
  getTargetData,
} from "../../../../utils/nutrition-helpers";

const NutritionHistoryTable = ({
  units,
  nutritionHistory,
  handleActiveDayChange,
  activeDayUtc,
  isActiveDayPeriod,
  nutritionPlans,
  isCheckIn = false,
  activeExercises,
}) => {
  const { t } = useTranslation("athlete-portal");
  return (
    <section role="table-wrapper" className="nutrition-table" data-scrollable>
      <table role="report-table">
        <thead>
          <tr className="top-header">
            <th colSpan={1} className="rounded-tl-lg">
              <span></span>
            </th>
            <th colSpan={5}>
              <span className="clipped">{t("nutritionPage.historyTable.target")}</span>
            </th>
            <th colSpan={5}>
              <span className="clipped">{t("nutritionPage.historyTable.consumed")}</span>
            </th>
            <th colSpan={5}>
              <span className="clipped">{t("nutritionPage.historyTable.adherence")}</span>
            </th>
            {!isCheckIn && <th colSpan={4} className="rounded-tr-lg"></th>}
          </tr>

          <BottomRown units={units} isCheckIn={isCheckIn} t={t} />
        </thead>
        <tbody>
          {nutritionHistory.map(
            ({
              nutrients,
              utcDate,
              hasWorkout,
              steps,
              bodyWeight,
              bodyWeightDays,
              isPeriod,
              startOfWeek,
              endOfWeek,
              nutrientsDays,
              proteinsDays,
              fatsDays,
              carbsDays,
              fibersDays,
              stepsDays,
              sleep,
              isSingle,
              workoutDays,
            }) => {
              const nutritionPlan = getNutritionPlan(
                nutritionPlans,
                utcDate,
                isPeriod,
                startOfWeek,
                endOfWeek
              );

              const nutritionData = getNutritionData(
                nutrients,
                units,
                steps,
                isPeriod,
                bodyWeight,
                bodyWeightDays,
                nutrientsDays,
                stepsDays,
                sleep,
                proteinsDays,
                carbsDays,
                fatsDays,
                fibersDays
              );

              const targetData = getTargetData({
                nutritionPlan,
                isPeriod,
                startOfWeek,
                endOfWeek,
                averageValues: true,
                hasWorkout,
                workoutDays,
                activeExercises,
              });

              // Condition so it doesn't show days where the user just uploaded body pictures or other stats like that
              const showRow =
                Boolean(nutritionData.kcal) ||
                Boolean(nutritionData.steps) ||
                Boolean(nutritionData.convertedBodyWeight) ||
                isPeriod;

              return (
                showRow && (
                  <NutritionHistoryTableRow
                    key={`${utcDate}-${nutritionData.kcal}-${
                      isPeriod || false
                    }`}
                    activeDayUtc={activeDayUtc}
                    handleActiveDayChange={handleActiveDayChange}
                    hasWorkout={hasWorkout}
                    utcDate={utcDate}
                    targetData={targetData}
                    nutritionData={nutritionData}
                    startOfWeek={startOfWeek}
                    endOfWeek={endOfWeek}
                    isActiveDayPeriod={isActiveDayPeriod}
                    isCheckIn={isCheckIn}
                    isSingle={isSingle}
                  />
                )
              );
            }
          )}
        </tbody>
      </table>
    </section>
  );
};

const BottomRown = ({ units, isCheckIn, t }) => (
  <tr>
    <th>{t("nutritionPage.historyTable.date")}</th>
    <th className="protein">
      {t("nutritionPage.historyTable.protein")}
      <br />
      <p className="st1">(g)</p>
    </th>
    <th className="carbs">
      {t("nutritionPage.historyTable.carbs")}
      <br />
      <p className="st1">(g)</p>
    </th>
    <th className="fat">
      {t("nutritionPage.historyTable.fat")}
      <br />
      <p className="st1">(g)</p>
    </th>
    <th>
      {t("nutritionPage.historyTable.fiber")}
      <br />
      <p className="st1">(g)</p>
    </th>
    <th>
      {t("nutritionPage.historyTable.kcal")}
      <br />
      <p className="st1"></p>
    </th>
    <th className="protein">
      {t("nutritionPage.historyTable.protein")}
      <br />
      <p className="st1">(g)</p>
    </th>
    <th className="carbs">
      {t("nutritionPage.historyTable.carbs")}
      <br />
      <p className="st1">(g)</p>
    </th>
    <th className="fat">
      {t("nutritionPage.historyTable.fat")}
      <br />
      <p className="st1">(g)</p>
    </th>
    <th>
      {t("nutritionPage.historyTable.fiber")}
      <br />
      <p className="st1">(g)</p>
    </th>
    <th>{t("nutritionPage.historyTable.kcal")}</th>
    <th className="protein">
      {t("nutritionPage.historyTable.protein")}
      <br />
      <p className="st1">(g)</p>
    </th>
    <th className="carbs">
      {t("nutritionPage.historyTable.carbs")}
      <br />
      <p className="st1">(g)</p>
    </th>
    <th className="fat">
      {t("nutritionPage.historyTable.fat")}
      <br />
      <p className="st1">(g)</p>
    </th>
    <th>
      {t("nutritionPage.historyTable.fiber")}
      <br />
      <p className="st1">(g)</p>
    </th>
    <th>{t("nutritionPage.historyTable.kcal")}</th>
    {!isCheckIn && (
      <>
        <th>
          {t("nutritionPage.historyTable.bodyweight")}
          <br />
          <span>{units === "metric" ? t("nutritionPage.historyTable.kg") : t("nutritionPage.historyTable.lbs")}</span>
        </th>
        <th>{t("nutritionPage.historyTable.steps")}</th>
        <th>{t("nutritionPage.historyTable.sleep")}</th>
        <th>{t("nutritionPage.historyTable.workout")}</th>
      </>
    )}
  </tr>
);

export default NutritionHistoryTable;
